import * as React from "react";
import {Provider, observer} from "mobx-react";
import {configure, makeObservable, observable} from "mobx";
import {NewUserRegistration} from "./ui/modules/user/NewUserRegistration";
import {ForgottenPasswordReset} from "./ui/modules/user/ForgottenPasswordReset";
import {GraphicalTools} from "./GraphicalTools";
import type {IModel} from "./data/models/Model";
import {DetailsContainer} from "./ui/modules/DetailsContainer";
import {getNewDetailedItems, onDetailsContainerClose} from "./ui/search/DetailsContainerUtils";
import {Root} from "./ui/5.0/Root";
import {TransportLayer} from "./data/TransportLayer";
import {GetAuthenticationRelatedLoadingTexts, Navigation} from "./Navigation";
import {KeyboardListener} from "./utils/interaction/key/KeyboardListener";
import {XHRLoader} from "./utils/loader/XHRLoader";
import {LoginWindow} from "./ui/windows/login/LoginWindow";
import {TopBar} from "./ui/topbar/TopBar";
import type {FeatureMap} from "./data/state/AppStateConstants";
import {AppState} from "./data/state/AppState";
import type {IXyiconsOutsideCameraFrustumRow} from "./data/state/AppActions";
import {SideBar} from "./ui/sidebar/SideBar";
import type {IConfig} from "./IConfig";
import type {AccessTokenDetailsDto, ValidateSpaceDetailsValidateObjectDto, ValidateSpaceRequest} from "./generated/api/base";
import {XyiconFeature} from "./generated/api/base";
import {PopupManager} from "./ui/modules/abstract/popups/manager/PopupManager";
import type {ReportView} from "./ui/modules/report/ReportView";
import {AppNotifications} from "./ui/notification/AppNotifications";
import {LoginWindowV5} from "./ui/5.0/login/LoginWindowV5";
import {ForgottenPasswordResetV5} from "./ui/5.0/user/ForgottenPasswordResetV5";
import {NewUserRegistrationV5} from "./ui/5.0/user/NewUserRegistrationV5";
import {AppNotificationsV5} from "./ui/5.0/popup/AppNotificationsV5";
import {useAppStore} from "./StateManager";
import {LoaderV5} from "./ui/5.0/loader/LoaderV5";
import {HTMLUtils} from "./utils/HTML/HTMLUtils";
import {PhotoSphereViewer} from "./ui/5.0/photosphere/PhotoSphereViewer";
import {FullscreenLoader} from "./FullscreenLoader";
import {SSOLoginFail} from "./ui/windows/SSOLogin/SSOAuthFail";
import {SSOLoginWindowV5} from "./ui/5.0/login/SSOLoginWindowV5";
import {SSOLoginWindow} from "./ui/windows/login/SSOLoginWindow";
import {Signal} from "./utils/signal/Signal";
import {StringUtils} from "./utils/data/string/StringUtils";
import {
	GetOrganizationWideNavigationMenusWithoutSubMenus,
	GetPortfolioWideNavigationMenus,
	NavigationEnum,
	PermissionSetSubNav,
	SettingsModuleNav,
	SettingsModuleSubNav,
	SettingsPermissionsSubNav,
	SettingsSubNav,
} from "./Enums";
import {LoginPopup} from "./ui/LoginPopup";
import {notify} from "./utils/Notify";
import type {INotificationParamsV5} from "./ui/5.0/popup/NotificationV5";
import {NotificationType} from "./ui/5.0/popup/NotificationV5";
import {
	getGeneralValidationErrorMessage,
	getGeneralValidationErrorTitle,
	processValidationResult,
	ReasonForNotAvailable,
} from "./ui/5.0/modules/ValidationUtils";

// TODO: remove this, and actually run the MobX related assignments in "actions"
configure({
	enforceActions: "never",
});

// https://stackoverflow.com/questions/12709074/how-do-you-explicitly-set-a-new-property-on-window-in-typescript
declare global {
	interface Window {
		app: App;
		listXyiconsOutsideCameraFrustum: () => IXyiconsOutsideCameraFrustumRow[];
	}
}

interface IAppState {
	isWaiting: boolean;
	navigationOpen: boolean;
	isLoginNeededToProceed: boolean;
}

interface ISelectView {
	selectItem?: (item: any) => void;
	focusItem?: (item: any) => void; // in ModuleViewV5 the selectItem() called focusItem()
}

@observer
export class App extends React.PureComponent<any, IAppState> {
	public config: IConfig;

	private readonly _appState = new AppState(this);
	private _transport: TransportLayer;
	private _navigation: Navigation;
	private _graphicalTools: GraphicalTools;
	private readonly _popups = React.createRef<PopupManager>();
	private readonly _modalContainer = React.createRef<HTMLDivElement>();
	private readonly _topBarRef = React.createRef<TopBar>();
	public readonly notificationContainer = React.createRef<AppNotifications>();
	public readonly moduleViews: FeatureMap<ISelectView> = {};
	public reportView: ReportView; // TODO: maybe V5 needs it also

	@observable
	public overlayedDetailedItems: {
		model: IModel;
		isMainBlurred: boolean;
	}[] = [];

	private readonly _sideBar = React.createRef<HTMLDivElement>();
	public readonly signals = {
		didUpdate: Signal.create<void>(),
	};

	constructor(props: null) {
		super(props);
		makeObservable(this);

		this.state = {
			isWaiting: true,
			navigationOpen: false,
			isLoginNeededToProceed: false,
		};

		window.app = this;
		window.listXyiconsOutsideCameraFrustum = () => this._appState.actions.listXyiconsOutsideCameraFrustum();
	}

	// This method get invoked when SSO login is successful
	// It receives a base64 encoded string as a message
	// We need to decode this string and parse it to get the necessary data
	private parseUrlParams(queryString: string) {
		if (!queryString) {
			return {};
		}
		// Decoding the base64 encoded string
		queryString = atob(queryString);
		const params: AccessTokenDetailsDto = {};
		const regex = /([^&]+)=([^&]+)/g; // Regex to capture key-value pairs
		let match: RegExpExecArray;

		while ((match = regex.exec(queryString)) !== null) {
			const key = decodeURIComponent(match[1])
				.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
				.replace(/([a-z])([Ii][Dd])$/, (_, letter1) => `${letter1}ID`)
				.replace(/Sso/, "SSO"); // Convert to camelCase only ID fields
			let value: any = decodeURIComponent(match[2]);
			// Convert specific values to the appropriate types
			if (/^\d+$/.test(value)) {
				value = parseInt(value, 10);
			} else if (value === "True" || value === "False") {
				value = value === "True";
			}

			(params as any)[key] = value;
		}
		return params;
	}

	private async init() {
		XHRLoader.defaultConfig.json = true;

		this.config = await XHRLoader.loadAsync({url: "src/config/config.json"});

		this._transport = new TransportLayer(this._appState, this.config);
		this._graphicalTools = new GraphicalTools(this);
		this._navigation = new Navigation(this._transport);
		this._transport.init();

		KeyboardListener.getInstance();
		document.body.addEventListener("paste", this.onPaste);
		window.addEventListener("hashchange", this.onHashChange);
		window.addEventListener("popstate", this.onHashChange);
		this._navigation.signals.replaceState.add(this.onHashChange);

		this._transport.signals.error.add(this.onError);
		this._transport.signals.switchOrganization.add(this.onAfterOrganizationSwitch);

		const {hash} = this;
		if (hash.includes("auth/ssocallback")) {
			this.setState({isWaiting: true});
			const queryParams: URLSearchParams = new URLSearchParams(hash.split("?")[1]);
			const queryObject: {isSuccess: boolean; message: string} = {isSuccess: false, message: ""};

			queryParams.forEach((value, key) => {
				const decodedKey = decodeURIComponent(key);
				const decodedValue = decodeURIComponent(value);

				if (decodedKey === "isSuccess") {
					queryObject[decodedKey] = decodedValue === "true"; // string → boolean
				} else if (decodedKey === "message") {
					queryObject[decodedKey] = decodedValue; // keep as string
				}
			});

			const {isSuccess, message} = queryObject;

			if (!isSuccess) {
				// When the isSuccess is false it receives a normal message
				this._navigation.goToSSOLoginFailed(isSuccess, message);
			} else {
				// When the isSuccess is true it receives the message as a base64 encoded string
				const ssoLoginData = this.parseUrlParams(message);
				const results = await this._transport.services.auth.loginWithSSO(ssoLoginData);
				if (results.error) {
					this._navigation.goToSSOLogin();
				}
				this._navigation.redirectAfterLogin();
				this._transport.services.auth.loadSecondaryList();
			}
		} else {
			const autoLogin: AccessTokenDetailsDto | {error: string} = await this._transport.services.auth.tryAutoLogin();
			if ((autoLogin as {error: string}).error) {
				if (window.location.href.includes("auth/new_user") || window.location.href.includes("auth/forgotten_password")) {
					this.forceUpdate();
				} else {
					if (hash === "auth/login/sso") {
						this._navigation.goToSSOLogin();
					} else {
						this._navigation.goToLogin();
					}
				}
			} else {
				const hash = this._navigation.redirectAfterLogin();
				if (hash) {
					this.forceUpdate();
				}
				this._transport.services.auth.loadSecondaryList();
			}
		}

		this.setState({isWaiting: false});
		if (this.transport.services.localStorage.get("isActivePortfolioDeleted")) {
			this._appState.actions.goToPortfolios();
			this.transport.services.localStorage.remove("isActivePortfolioDeleted");
		}
	}

	private onError = async (xhr: XHRLoader) => {
		this._appState.lastError = "Session expired, please log in again.";
		await this._transport.services.auth.logout();
	};

	private onAfterOrganizationSwitch = () => {
		// Reload to make sure all data is reloaded again with the correct (new) organization
		location.reload();
	};

	private onCloseSideBar = (event: MouseEvent) => {
		const sideBar = this._sideBar.current;

		if (sideBar) {
			if (!sideBar.contains(event.target as HTMLElement)) {
				// Clicked outside of SideBar -> close it
				window.removeEventListener("click", this.onCloseSideBar);
				this.setState({navigationOpen: false});
			}
		}
	};

	private normalizeHashToLowercase() {
		const hash = window.location.hash || "";
		if (!hash.startsWith("#")) {
			return;
		}

		const withoutHash = hash.slice(1);
		const [pathPart, queryPart] = withoutHash.split("?", 2);

		const lowerPath = pathPart.toLowerCase();
		if (lowerPath === pathPart) {
			return;
		}

		const newHash = `#${lowerPath}${queryPart != null ? `?${queryPart}` : ""}`;

		// Does NOT add a history entry (unlike setting location.hash = ...)
		window.location.replace(window.location.pathname + window.location.search + newHash);
	}

	private onHashChange = async () => {
		this.normalizeHashToLowercase();
		const {hash} = this;

		if (StringUtils.cleanHash(this._navigation.previousHash) !== hash) {
			if (this._appState.selectedMenu !== NavigationEnum.SETTINGS) {
				this._navigation.lastNonSettingsHash = `#${hash}`;
			}
			this._navigation.previousHash = `#${hash}`;

			if (this._transport.appState.user || this.hash.includes("auth")) {
				await this.validateStateByURLHash();
				this.forceUpdate();
			} else {
				this._navigation.goToLogin();
			}
		}
	};

	protected onPaste = (event: ClipboardEvent) => {
		if (event.clipboardData?.files.length > 0) {
			this.loadFiles(Array.from(event.clipboardData.files));
		}
	};

	public loadFiles(files: File[]) {
		// TODO ?
	}

	private readonly onToggleNavigation = () => {
		this.setState((prevState) => ({navigationOpen: !prevState.navigationOpen}));
	};

	public readonly onDetailsClick = (item: IModel, replaceExistingItemsInArray: boolean = false, isMainBlurred: boolean = true) => {
		this.overlayedDetailedItems = replaceExistingItemsInArray
			? [{model: item, isMainBlurred}]
			: getNewDetailedItems(this.overlayedDetailedItems, {model: item, isMainBlurred});
	};

	public onDetailsContainerClose = (closeAll: boolean = false) => {
		onDetailsContainerClose(this, closeAll);
	};

	private readonly closeDetailsPanel = () => {
		this.onDetailsContainerClose(true);
	};

	public get graphicalTools() {
		return this._graphicalTools;
	}

	public get transport() {
		return this._transport;
	}

	public get navigation() {
		return this._navigation;
	}

	public get spaceViewRenderer() {
		return this._graphicalTools.spaceViewRenderer;
	}

	public get popups() {
		return this._popups.current;
	}

	public get modalContainer() {
		return this._modalContainer.current;
	}

	private renderView(): React.JSX.Element {
		if (this._appState.currentUIVersion === "5.0") {
			document.body.classList.remove("V4");
			document.body.classList.add("V5");
		} else {
			document.body.classList.remove("V5");
			document.body.classList.add("V4");
		}

		const hash = this.hash;
		if (hash === "auth/login/sso") {
			if (this._appState.currentUIVersion === "5.0") {
				return <SSOLoginWindowV5 />;
			} else {
				return <SSOLoginWindow />;
			}
		} else if (hash === "auth/login") {
			if (this._appState.currentUIVersion === "5.0") {
				return <LoginWindowV5 />;
			} else {
				return <LoginWindow />;
			}
		} else if (hash.includes("auth/sso-login-failed")) {
			return <SSOLoginFail />;
		} else if (hash.includes("auth/new_user")) {
			return (
				<>
					<div
						id={HTMLUtils.modalContainerId}
						ref={this._modalContainer}
					/>
					<div className="page">
						{this._appState.currentUIVersion === "5.0" ? (
							<>
								<AppNotificationsV5 ref={this.notificationContainer as unknown as React.RefObject<AppNotificationsV5>} />
								<NewUserRegistrationV5 />
							</>
						) : (
							<>
								<AppNotifications ref={this.notificationContainer} />
								<NewUserRegistration />
							</>
						)}
					</div>
				</>
			);
		} else if (hash.includes("auth/forgotten_password")) {
			return (
				<>
					<div
						id={HTMLUtils.modalContainerId}
						ref={this._modalContainer}
					/>
					<div className="page">
						{this._appState.currentUIVersion === "5.0" ? (
							<>
								<AppNotificationsV5 ref={this.notificationContainer as unknown as React.RefObject<AppNotificationsV5>} />
								<ForgottenPasswordResetV5 />
							</>
						) : (
							<>
								<AppNotifications ref={this.notificationContainer} />
								<ForgottenPasswordReset />
							</>
						)}
					</div>
				</>
			);
		} else {
			if (this._appState.currentUIVersion === "5.0") {
				return (
					this._appState.user && (
						<Root
							notificationContainer={this.notificationContainer}
							modalContainer={this._modalContainer}
							closeDetailsContainer={this.onDetailsContainerClose}
							closeWideSearchPanel={this._topBarRef.current?.mainSearchRef.current?.closeSearchInput}
						/>
					)
				);
			} else {
				const {activePhotoSphereMaybe} = this._graphicalTools.photoSphereManager;
				const {selectedMenu} = this._appState;
				const Component = this._appState.app.navigation.getComponent(selectedMenu);

				return (
					this._appState.user && (
						<>
							<AppNotifications ref={this.notificationContainer} />
							<div
								id={HTMLUtils.modalContainerId}
								ref={this._modalContainer}
							/>
							<TopBar
								ref={this._topBarRef}
								navigationOpen={this.state.navigationOpen}
								onToggleNavigation={this.onToggleNavigation}
							/>
							{activePhotoSphereMaybe && <PhotoSphereViewer activePhotoSphere={activePhotoSphereMaybe} />}
							<div className="main">
								<SideBar
									divRef={this._sideBar}
									open={this.state.navigationOpen}
									onToggleNavigation={this.onToggleNavigation}
								/>
								<article className="appContent">
									{hash.includes("app/") && <Component spaceViewRenderer={this._appState.app.spaceViewRenderer} />}
								</article>
							</div>
							<DetailsContainer
								items={this.overlayedDetailedItems.map((i) => i.model)}
								onClose={this.onDetailsContainerClose}
								closeWideSearchPanel={this._topBarRef.current?.mainSearchRef.current?.closeSearchInput}
							/>
							<PopupManager
								ref={this._popups}
								keyboardListener={KeyboardListener.getInstance()}
							/>
						</>
					)
				);
			}
		}
	}

	public get hash() {
		return StringUtils.cleanHash(location.hash ?? "");
	}

	public validateStateByURLHash = async () => {
		const isHashValid = StringUtils.isHashValid(this.hash, this._appState.currentUIVersion, this._appState.user);
		if (isHashValid && this._appState.user) {
			await this.navigation.switchOrganizationAndPortfolioIfNecessary(this.hash, this.forceUpdateArrow);
		}

		if (this._appState.user) {
			const parts = location.hash.split("/");
			let orgId: string = this._appState.organizationIdByAuthData;
			const orgIdMaybe = parts[1];

			if (
				orgIdMaybe &&
				StringUtils.uuidPattern.test(orgIdMaybe) &&
				orgIdMaybe !== orgId &&
				this._appState.organizations.some((o) => o.id === orgIdMaybe)
			) {
				orgId = orgIdMaybe;
			}

			const {
				selectedMenu,
				selectedSubMenu,
				selectedSettingsModuleMenu,
				selectedPermissionMenu,
				selectedSettingsModuleSubMenu,
				selectedPermissionSetSubMenu,
			} = this._appState;

			if (!isHashValid) {
				if (
					GetOrganizationWideNavigationMenusWithoutSubMenus(this._appState.currentUIVersion).includes(selectedMenu) ||
					(GetPortfolioWideNavigationMenus().includes(selectedMenu) && !this._appState.activePortfolioRefId)
				) {
					this._navigation.goApp({organizationId: orgId, mainNav: selectedMenu, replacePreviousHashInHistory: true});
					if (orgIdMaybe !== orgId) {
						this._navigation.notifyUserAboutWrongOrgId();
					}
				} else {
					switch (selectedMenu) {
						case NavigationEnum.SETTINGS:
							if (selectedSubMenu && this._appState.user?.isAdmin) {
								const subMenusNeed3rdLevel: SettingsSubNav[] = [SettingsSubNav.PERMISSIONS, SettingsSubNav.MODULES];
								const subNavs: string[] = [selectedSubMenu];
								if (subMenusNeed3rdLevel.includes(selectedSubMenu)) {
									if (selectedSubMenu === SettingsSubNav.PERMISSIONS) {
										if (selectedPermissionMenu === SettingsPermissionsSubNav.PERMISSION_SETS) {
											subNavs.push(selectedPermissionMenu, selectedPermissionSetSubMenu || PermissionSetSubNav.PORTFOLIOS);
										} else {
											subNavs.push(SettingsPermissionsSubNav.USERS);
										}
									} else {
										subNavs.push(
											selectedSettingsModuleMenu || SettingsModuleNav.PORTFOLIO,
											selectedSettingsModuleSubMenu || SettingsModuleSubNav.TYPES,
										);
									}
								}
								this._navigation.goApp({organizationId: orgId, mainNav: selectedMenu, subNavs, replacePreviousHashInHistory: true});
							} else {
								this._navigation.goApp({
									organizationId: orgId,
									mainNav: selectedMenu,
									subNavs: [SettingsSubNav.PERSONAL],
									replacePreviousHashInHistory: true,
								});
							}
							break;
						case NavigationEnum.SPACE:
						case NavigationEnum.DETAILS:
							this._navigation.goToDefault(orgId, true);
							break;
						default:
							this._navigation.goApp({organizationId: orgId, mainNav: selectedMenu, replacePreviousHashInHistory: true});
							break;
					}
				}

				return;
			} else if (this._appState.currentUIVersion === "4.0" && selectedMenu === NavigationEnum.PORTFOLIOS) {
				// Special case: in V4, if we have a last selected Portfolio, but the URL doesn't have it, we need to fix that
				const {activePortfolio, activePortfolioRefId} = this._appState;

				if ((activePortfolio?.refId ?? null) != activePortfolioRefId) {
					this._appState.app.navigation.goApp({
						organizationId: orgId,
						mainNav: NavigationEnum.PORTFOLIOS,
						subNavs: [activePortfolio?.refId ?? ""],
						replacePreviousHashInHistory: true,
					});
				}
			}

			await this.redirectToSpacesIfNeeded();
		} else if (!isHashValid) {
			this._appState.app.navigation.goToLogin();
		}
	};

	private redirectToSpacesIfNeeded = async () => {
		if (StringUtils.isHashValidForSpaceBase(this.hash)) {
			const redirectToSpacesWithToastMessage = async (unavailableSpaceRefId: string) => {
				this._navigation.goApp({
					mainNav: NavigationEnum.SPACES,
					replacePreviousHashInHistory: true,
				});

				const moduleName = "Space";

				const notificationParams: INotificationParamsV5 = {
					type: NotificationType.Warning,
					title: getGeneralValidationErrorTitle(moduleName),
					description: getGeneralValidationErrorMessage(moduleName),
				};

				if (/s[0-9]+$/g.test(unavailableSpaceRefId)) {
					const params: ValidateSpaceRequest = {
						spaceRefID: unavailableSpaceRefId,
					};

					const {result, error} = await this._transport.requestForOrganization<ValidateSpaceDetailsValidateObjectDto>({
						url: "spaces/validate",
						method: XHRLoader.METHOD_POST,
						params,
					});

					const notAvailableInfo = processValidationResult(moduleName, this._appState, result, error);

					if (notAvailableInfo) {
						notificationParams.title = notAvailableInfo.title;

						if (typeof notAvailableInfo.details === "string") {
							notificationParams.description = notAvailableInfo.details;
						}

						if (notAvailableInfo.reason === ReasonForNotAvailable.DifferentPortfolio) {
							const relevantPortfolio = this._appState.actions.getPortfolioById(result.details.portfolioID);
							if (relevantPortfolio) {
								const onPortfolioSwitchClick = () => {
									this.navigation.goApp({
										mainNav: NavigationEnum.SPACE,
										portfolioRefId: relevantPortfolio.refId,
										subNavs: [`${result.details.spaceRefID}`],
									});
								};
								notificationParams.onActionButtonClick = onPortfolioSwitchClick;
								notificationParams.buttonLabel = "Switch Portfolio";
							}
						}
					}
				}

				notify(this.notificationContainer, notificationParams);
			};

			if (StringUtils.isHashValidForSpace(this.hash)) {
				const spaceRefIdMaybe = StringUtils.getSpaceRefIdFromHash(this.hash);
				if (spaceRefIdMaybe) {
					await this._transport.services.feature.refreshList(XyiconFeature.Space);
					const spaceMaybe = this._appState.actions.getList<IModel>(XyiconFeature.Space).find((s) => s.refId === spaceRefIdMaybe);
					if (!spaceMaybe) {
						redirectToSpacesWithToastMessage(spaceRefIdMaybe);
					}
				}
			} else {
				redirectToSpacesWithToastMessage("");
			}
		}
	};

	private forceUpdateArrow = () => {
		this.forceUpdate();
	};

	private onCloseLoginPopup = () => {
		this.setState({
			isLoginNeededToProceed: false,
		});

		this._navigation.revertURLToActiveOrg();

		this._appState.fullscreenLoaderText = "";
	};

	private onSuccessfullyLoggedIn = () => {
		this.onCloseLoginPopup();
	};

	public override async componentDidMount() {
		this.normalizeHashToLowercase();
		document.querySelectorAll(".hardcodedBlack").forEach((item: SVGElement) => (item.style.fill = "black"));
		await this.init();
		await this.validateStateByURLHash();
		this._navigation.signals.navigate.add(this.closeDetailsPanel);
	}

	public override componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<IAppState>): void {
		if (this.state.navigationOpen && !prevState.navigationOpen) {
			requestAnimationFrame(() => {
				window.addEventListener("click", this.onCloseSideBar);
			});
		} else if (!this.state.navigationOpen && prevState.navigationOpen) {
			window.removeEventListener("click", this.onCloseSideBar);
		}

		this.signals.didUpdate.dispatch();
	}

	public override componentWillUnmount() {
		this._navigation?.signals.navigate.remove(this.closeDetailsPanel);
		this._navigation?.signals.replaceState.remove(this.onHashChange);
		window.removeEventListener("hashchange", this.onHashChange);
		window.removeEventListener("popstate", this.onHashChange);
	}

	public override render() {
		const hash = this.hash;

		if (
			!hash ||
			this.state.isWaiting ||
			hash.includes("auth/ssosignin") ||
			hash.includes("auth/ssocallback") ||
			GetAuthenticationRelatedLoadingTexts().includes(this._appState.fullscreenLoaderText)
		) {
			return (
				<div className="preLoadingPage">
					<LoaderV5 />
				</div>
			);
		}

		return (
			<Provider
				navigation={this._navigation}
				transport={this._transport}
				appState={this._appState}
				app={this}
			>
				<AppStoreInitializer appState={this._appState} />
				{this.renderView()}
				<LoginPopup
					onClose={this.onCloseLoginPopup}
					isVisible={this.state.isLoginNeededToProceed}
					appState={this._appState}
					handleSwitchFromSSOToNormalLoginSuccess={this.onSuccessfullyLoggedIn}
				/>
				<FullscreenLoader text={this._appState?.fullscreenLoaderText} />
			</Provider>
		);
	}
}

interface IAppStoreInitializer {
	readonly appState: AppState;
}

const AppStoreInitializer = (props: IAppStoreInitializer) => {
	const appState = useAppStore((state) => state.appState);
	const setAppState = useAppStore((state) => state.setAppState);

	if (!appState) {
		setAppState(props.appState);
	}

	return <></>;
};
